import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';
import {
  getDeliveryCountries,
  getDeliveryInfo,
  getNamedDayDeliveryDates,
  getPaymentInfo,
  getPaymentRequest,
  getPaymentRequestConfirmation,
  getPickupPoints,
  GetPickupPointsParams,
  getSaferpayAltStatus,
  getStoresForGeoLocation,
  GetStoresForGeoLocationParams,
  initRequest,
  statusSaferpayAlternative,
} from './checkout.api';
import type { OptionalQueryOptions, QueryApiOptions } from '../../types/types';
import {
  CheckoutInitSetState,
  ClickAndCollectStore,
  DeliveryCountriesResponse,
  DeliveryDate,
  DeliveryInfo,
  PaymentInfo,
  PaymentRequestConfirmation,
  PaymentRequestInfo,
  PickupPoint,
  SaferpayAltStatus,
} from '@as-react/common-types';
import { AxiosResponse } from 'axios';

export const checkoutQueryKeys = {
  all: () => ['checkout'] as QueryKey,
  init: () => [...checkoutQueryKeys.all(), 'init'] as QueryKey,
  getDeliveryCountries: () => [...checkoutQueryKeys.all(), 'deliveryCountries'] as QueryKey,
  getDeliveryInfo: (countryId?: number) => [...checkoutQueryKeys.all(), 'deliveryInfo', countryId] as QueryKey,
  getPickupPoints: (params: GetPickupPointsParams) =>
    [...checkoutQueryKeys.all(), 'getPickupPoints', params] as QueryKey,
  getStoresForGeoLocation: (params: GetStoresForGeoLocationParams) =>
    [...checkoutQueryKeys.all(), 'getStoresForGeoLocation', params] as QueryKey,
  getPaymentInfo: () => [...checkoutQueryKeys.all(), 'paymentInfo'] as QueryKey,
  getNamedDayDeliveryDates: () => [...checkoutQueryKeys.all(), 'namedDayDeliveryDates'] as QueryKey,
  getSaferpayAltStatus: (requestId: Parameters<typeof getSaferpayAltStatus>[1]) =>
    [...checkoutQueryKeys.all(), 'getSaferpayAltStatus', requestId] as QueryKey,
  getPaymentRequest: (params: string) => [...checkoutQueryKeys.all(), 'getPaymentRequest', params] as QueryKey,
  getPaymentRequestConfirmation: (params: string) =>
    [...checkoutQueryKeys.all(), 'getPaymentRequestConfirmation', params] as QueryKey,
  getStatusSaferpayAlternative: (params: string) =>
    [...checkoutQueryKeys.all(), 'statusSaferpayAlternative', params] as QueryKey,
};

export const checkoutQueries = (apiOptions: QueryApiOptions) => ({
  initRequest: (options: OptionalQueryOptions<CheckoutInitSetState> = {}): UseQueryOptions<CheckoutInitSetState> => ({
    ...options,
    queryKey: checkoutQueryKeys.init(),
    queryFn: () => initRequest(apiOptions),
  }),
  getDeliveryCountries: <TReturnData>(
    options: OptionalQueryOptions<DeliveryCountriesResponse, TReturnData> = {}
  ): UseQueryOptions<DeliveryCountriesResponse, Error, TReturnData> => ({
    ...options,
    queryKey: checkoutQueryKeys.getDeliveryCountries(),
    queryFn: () => getDeliveryCountries(apiOptions),
  }),
  getDeliveryInfo: <TReturnData = DeliveryInfo>(
    countryId?: number,
    options: OptionalQueryOptions<DeliveryInfo, TReturnData> = {}
  ): UseQueryOptions<DeliveryInfo, Error, TReturnData> => ({
    ...options,
    queryKey: checkoutQueryKeys.getDeliveryInfo(countryId),
    queryFn: () => getDeliveryInfo(apiOptions, countryId),
  }),
  getPickupPoints: <TReturnData = AxiosResponse<Array<PickupPoint>>>(
    params: GetPickupPointsParams,
    options: OptionalQueryOptions<AxiosResponse<Array<PickupPoint>>, TReturnData> = {}
  ): UseQueryOptions<AxiosResponse<Array<PickupPoint>>, Error, TReturnData> => ({
    ...options,
    queryKey: checkoutQueryKeys.getPickupPoints(params),
    queryFn: () => getPickupPoints(apiOptions, params),
  }),
  getStoresForGeoLocation: (
    params: GetStoresForGeoLocationParams,
    options: OptionalQueryOptions<Array<ClickAndCollectStore>>
  ): UseQueryOptions<Array<ClickAndCollectStore>> => ({
    ...options,
    queryKey: checkoutQueryKeys.getStoresForGeoLocation(params),
    queryFn: () => getStoresForGeoLocation(apiOptions, params),
  }),
  getPaymentInfo: <TReturnData = PaymentInfo>(
    options: OptionalQueryOptions<PaymentInfo, TReturnData> = {}
  ): UseQueryOptions<PaymentInfo, Error, TReturnData> => ({
    ...options,
    queryKey: checkoutQueryKeys.getPaymentInfo(),
    queryFn: () => getPaymentInfo(apiOptions),
  }),
  getNamedDayDeliveryDates: (
    options: OptionalQueryOptions<Array<DeliveryDate>> = {}
  ): UseQueryOptions<Array<DeliveryDate>> => ({
    ...options,
    queryKey: checkoutQueryKeys.getNamedDayDeliveryDates(),
    queryFn: () => getNamedDayDeliveryDates(apiOptions),
  }),
  getSaferpayAltStatus: <TReturnData = SaferpayAltStatus>(
    requestId: Parameters<typeof getSaferpayAltStatus>[1],
    options: OptionalQueryOptions<SaferpayAltStatus, TReturnData> = {}
  ): UseQueryOptions<SaferpayAltStatus, Error, TReturnData> => ({
    ...options,
    queryKey: checkoutQueryKeys.getSaferpayAltStatus(requestId),
    queryFn: () => getSaferpayAltStatus(apiOptions, requestId),
  }),
  getPaymentRequest: (
    params: string,
    options: OptionalQueryOptions<PaymentRequestInfo> = {}
  ): UseQueryOptions<PaymentRequestInfo, Error, PaymentRequestInfo> => ({
    ...options,
    queryKey: checkoutQueryKeys.getPaymentRequest(params),
    queryFn: () => getPaymentRequest(apiOptions, params),
  }),
  getPaymentRequestConfirmation: (
    params: string,
    options: OptionalQueryOptions<PaymentRequestConfirmation> = {}
  ): UseQueryOptions<PaymentRequestConfirmation, Error, PaymentRequestConfirmation> => ({
    ...options,
    queryKey: checkoutQueryKeys.getPaymentRequestConfirmation(params),
    queryFn: () => getPaymentRequestConfirmation(apiOptions, params),
  }),
  getStatusSaferpayAlternative: (
    params: string,
    options: OptionalQueryOptions<SaferpayAltStatus> = {}
  ): UseQueryOptions<SaferpayAltStatus, Error, SaferpayAltStatus> => ({
    ...options,
    queryKey: checkoutQueryKeys.getStatusSaferpayAlternative(params),
    queryFn: () => statusSaferpayAlternative(apiOptions, params),
  }),
});
